
import { Trans, t } from '@lingui/macro';
import { useState } from "react";
import { useHistory } from "../../hooks/useRouterCompat";
import Page from "../../const/Page";
import { useAppUIContext } from "../../context/AppUIContext";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { TextEmbedder } from "../../models/local/Types";
import { PlusCircle, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useToast } from "../../components/ui/use-toast";

export const SoribotCreateModal: React.FC<{ workspaceId: string | null }> = ({ workspaceId }) => {
  const { workspaces } = useAppUIContext();
  const [botWorkspaceId, setBotWorkspaceId] = useState<string | null>(workspaceId);
  const [embeddingModel, setEmbeddingModel] = useState<TextEmbedder>('text-embedding-ada-002');
  const [botName, setBotName] = useState<string>('');
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const history = useHistory();
  const [inProgress, setInProgress] = useState(false);

  async function createBot() {
    if (botWorkspaceId && botName) {
      setInProgress(true);
      Fire.bot.createSoribot({ name: botName, workspaceId: botWorkspaceId, roomId: undefined, owner: My.user, embeddingModel: embeddingModel })
        .then(bot => {
          toast({ description: t`Soribot ${bot.name} created.` });
          history.push(`${Page.soribot}/details/${bot.id}`);
          setOpen(false);
        })
        .catch(error => {
          toast({ variant: "destructive", description: t`Failed to create a Soribot. ${error.message}` });
        })
        .finally(() => {
          setInProgress(false);
        });
    } else {
      toast({ variant: "destructive", description: t`Workspace is required` });
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <PlusCircle className="w-4 h-4" />
          <span className="hidden sm:inline"><Trans>New Soribot</Trans></span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle><Trans>Create a new Soribot</Trans></DialogTitle>
          <DialogDescription><Trans>Enter details to create your new bot.</Trans></DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="name" className="text-right">
              <Trans>Bot name</Trans>
            </Label>
            <Input
              id="name"
              value={botName}
              onChange={(e) => setBotName(e.target.value)}
              placeholder={t`Bot name`}
              maxLength={35}
              className="col-span-3"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="workspace" className="text-right">
              <Trans>Workspace</Trans>
            </Label>
            <Select value={botWorkspaceId ?? undefined} onValueChange={setBotWorkspaceId}>
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder={t`Select workspace`} />
              </SelectTrigger>
              <SelectContent>
                {workspaces.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="model" className="text-right">
              <Trans>Embedding</Trans>
            </Label>
            <Select value={embeddingModel} onValueChange={(val) => setEmbeddingModel(val as TextEmbedder)}>
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder={t`Select`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='text-embedding-ada-002'>text-embedding-ada-002 (<Trans>stable</Trans>)</SelectItem>
                <SelectItem value='text-embedding-3-small'>text-embedding-3-small (<Trans>new</Trans>)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={createBot} disabled={inProgress}>
            {inProgress && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Trans>Create</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default SoribotCreateModal;